import {Node, XYPosition} from 'reactflow';

import {LAYOUT_LANE_SPACING} from './autoLayout';


export const NODE_FOOTPRINT = {width: 220, height: 120};

export const NODE_CENTRE_OFFSET: XYPosition = {
    x: NODE_FOOTPRINT.width / 2,
    y: NODE_FOOTPRINT.height / 2,
};

const PLACEMENT_STEP: XYPosition = {x: 40, y: 40};

const PLACEMENT_GAP = 16;

const MAX_PLACEMENT_ATTEMPTS = 40;

const MAX_LANE_ATTEMPTS = 8;

/** The picker a row is dragged from: its full-screen layer and invisible backdrop are looked through. */
const PICKER_LAYER = '.MuiPopover-root, .MuiPopover-root > .MuiBackdrop-root';

const getPaneRect = (): DOMRect | null => (
    document.querySelector('.react-flow__pane')?.getBoundingClientRect() ?? null
);

/** True only on the canvas itself, not on the open picker or a canvas panel above it. */
export const isInsidePane = (clientX: number, clientY: number): boolean => {
    const rect = getPaneRect();
    if (!rect) return false;

    const insideRect = clientX >= rect.left
        && clientX <= rect.right
        && clientY >= rect.top
        && clientY <= rect.bottom;

    // jsdom has no hit testing; there the rectangle is all that can be checked.
    if (!insideRect || typeof document.elementsFromPoint !== 'function') return insideRect;

    const hit = document.elementsFromPoint(clientX, clientY).find(element => !element.matches(PICKER_LAYER));
    return !!hit?.closest('.react-flow__pane');
};

export const getPaneCentre = (): XYPosition => {
    const rect = getPaneRect();
    if (!rect) return {x: window.innerWidth / 2, y: window.innerHeight / 2};

    return {x: rect.left + rect.width / 2, y: rect.top + rect.height / 2};
};

const overlaps = (candidate: XYPosition, node: Node): boolean => {
    const width = node.width ?? NODE_FOOTPRINT.width;
    const height = node.height ?? NODE_FOOTPRINT.height;

    return candidate.x < node.position.x + width + PLACEMENT_GAP
        && candidate.x + NODE_FOOTPRINT.width + PLACEMENT_GAP > node.position.x
        && candidate.y < node.position.y + height + PLACEMENT_GAP
        && candidate.y + NODE_FOOTPRINT.height + PLACEMENT_GAP > node.position.y;
};

export const findOpenPosition = (preferred: XYPosition, nodes: Node[]): XYPosition => {
    for (let attempt = 0; attempt <= MAX_PLACEMENT_ATTEMPTS; attempt++) {
        const candidate: XYPosition = {
            x: preferred.x + attempt * PLACEMENT_STEP.x,
            y: preferred.y + attempt * PLACEMENT_STEP.y,
        };

        if (!nodes.some(node => overlaps(candidate, node))) return candidate;
    }

    return preferred;
};

// Same row, whole lanes left/right (aligned with Auto layout); the diagonal cascade once the row is full.
export const findOpenLanePosition = (preferred: XYPosition, nodes: Node[]): XYPosition => {
    for (let attempt = 0; attempt <= MAX_LANE_ATTEMPTS; attempt++) {
        const distance = Math.ceil(attempt / 2) * LAYOUT_LANE_SPACING;
        const candidate: XYPosition = {
            x: preferred.x + (attempt % 2 === 1 ? distance : -distance),
            y: preferred.y,
        };

        if (!nodes.some(node => overlaps(candidate, node))) return candidate;
    }

    return findOpenPosition(preferred, nodes);
};
